import { useEffect, useRef, useState } from "react";
import AdminTopNavBar from "../common/AdminTopNavBar";
import getUserMenuItems from "./AdminMenuContent";
import AdminSideBar from "./AdminSideBar";
import { adminReqConfig, api } from "../../include/api";
import AdminFooter from "../common/AdminFooter";
import "../../styles/CategoryProductList.css";
import { ChevronDown, X } from "react-feather";
import { getAdmin } from "../../include/function";

const toISODate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCurrentWeekRange = () => {
  const now = new Date();
  const day = now.getDay();
  const diffToMonday = day === 0 ? 6 : day - 1;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(now.getDate() - diffToMonday);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { fromDate: toISODate(start), toDate: toISODate(end) };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ListAuditLog = () => {
  const homeLink = "/admin";
  const weekRange = getCurrentWeekRange();
  const [fetchLoading, setFetchLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const toggleSidebar = () => setIsSidebarMinimized(!isSidebarMinimized);
  const menuContent = getUserMenuItems();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const user = getAdmin();
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterValues, setFilterValues] = useState(weekRange);
  const [appliedFilters, setAppliedFilters] = useState(weekRange);
  const [canView, setCanView] = useState(() => {
    if (user?.createdBy === "super") return true;
    const perm = (user?.roleProperties || []).find(
      (item) => item.pageUrl === "/admin/audit-log" || item.pageTitle === "Audit Log",
    );
    return perm?.view === "true" || perm?.view === 1;
  });
  const filterWrapperRef = useRef(null);

  useEffect(() => {
    if (user?.createdBy === "super") {
      setCanView(true);
      return;
    }
    if (!user?.roleId) return;

    api
      .get(`/role_permission/?roleId=${user.roleId}`, adminReqConfig())
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data.data)) {
          const perm = res.data.data.find((p) => p.pageTitle === "Audit Log");
          setCanView(perm?.view === "true" || perm?.view === 1);
        }
      })
      .catch(() => setCanView(false));
  }, [user?.roleId, user?.createdBy]);

  const getAuditLogs = (filters) => {
    setFetchLoading(true);
    const params = new URLSearchParams();
    if (filters.fromDate) params.set("fromDate", filters.fromDate);
    if (filters.toDate) params.set("toDate", filters.toDate);

    api
      .get(`/audit_log/?${params.toString()}`, adminReqConfig())
      .then((response) => {
        const rows = response.status === 200 ? response.data.data || [] : [];
        setAuditLogs(rows);
        setFilteredData(rows);
      })
      .catch(() => {
        setAuditLogs([]);
        setFilteredData([]);
      })
      .finally(() => setFetchLoading(false));
  };

  useEffect(() => {
    if (canView) getAuditLogs(appliedFilters);
  }, [canView, appliedFilters]);

  useEffect(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      setFilteredData(auditLogs);
      setCurrentPage(1);
      return;
    }

    setFilteredData(
      auditLogs.filter((row) =>
        [
          row.module,
          row.action,
          row.referenceId,
          row.oldValue,
          row.newValue,
          row.createdByName,
          row.createdBy,
        ]
          .join(" ")
          .toLowerCase()
          .includes(query),
      ),
    );
    setCurrentPage(1);
  }, [searchQuery, auditLogs]);

  let currentItems = [];
  let totalPage = 1;
  let showingFrom = 0;
  let showingTo = 0;

  if (itemsPerPage === "all") {
    currentItems = filteredData;
    showingFrom = filteredData.length > 0 ? 1 : 0;
    showingTo = filteredData.length;
  } else {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    totalPage = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
    showingFrom = filteredData.length === 0 ? 0 : indexOfFirstItem + 1;
    showingTo = Math.min(indexOfLastItem, filteredData.length);
  }

  const applyFilters = () => {
    setAppliedFilters({ ...filterValues });
    setIsFilterPanelOpen(false);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    const range = getCurrentWeekRange();
    setFilterValues(range);
    setAppliedFilters(range);
    setSearchQuery("");
    setIsFilterPanelOpen(false);
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isFilterPanelOpen &&
        filterWrapperRef.current &&
        !filterWrapperRef.current.contains(event.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFilterPanelOpen]);

  return (
    <>
      <AdminSideBar menuContent={menuContent} isMinimized={isSidebarMinimized} />
      <AdminTopNavBar
        toggleSidebar={toggleSidebar}
        isSidebarMinimized={isSidebarMinimized}
        homeLink={homeLink}
        title="Audit Log"
      />
      <div
        className="content-wrapper p-0"
        style={{ marginLeft: isSidebarMinimized ? "80px" : "250px", flex: "1" }}
      >
        <section
          style={{
            backgroundColor: "#F1F4F9",
            padding: "30px",
            marginTop: "70px",
            marginBottom: "40px",
          }}
        >
          {!canView ? (
            <div className="alert alert-danger mb-0">Access denied</div>
          ) : fetchLoading ? (
            <p>Loading Data...</p>
          ) : (
            <div className="ps-22 pe-22 pb-22 rounded-5" style={{ backgroundColor: "#fff" }}>
              <div className="row align-items-center pt-26 mb-3">
                <div className="col-auto">
                  <h6 className="mb-0">Audit Log</h6>
                </div>
                <div className="col d-flex align-items-center">
                  <div
                    className="filter-wrapper"
                    style={{ position: "relative", width: "100%", maxWidth: "500px" }}
                    ref={filterWrapperRef}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid #c6d6f5",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        width: "100%",
                      }}
                    >
                      <img
                        src="/assets/images/search-icon.png"
                        alt=""
                        style={{ width: "18px", marginLeft: "8px" }}
                      />
                      <input
                        type="text"
                        placeholder="Filter and search here..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setIsFilterPanelOpen(true)}
                        style={{
                          border: "none",
                          outline: "none",
                          padding: "8px 8px",
                          width: "100%",
                          fontSize: "14px",
                          color: "#333",
                          backgroundColor: "transparent",
                        }}
                      />
                      {searchQuery && (
                        <X
                          size={24}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchQuery("");
                            setCurrentPage(1);
                          }}
                          style={{
                            position: "absolute",
                            right: "40px",
                            cursor: "pointer",
                            color: "#999",
                            padding: "4px",
                          }}
                        />
                      )}
                      <ChevronDown
                        size={16}
                        style={{
                          marginRight: "8px",
                          color: "#999",
                          cursor: "pointer",
                          transform: isFilterPanelOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsFilterPanelOpen(!isFilterPanelOpen);
                        }}
                      />
                    </div>
                    {isFilterPanelOpen && (
                      <FilterPanel
                        filterValues={filterValues}
                        onFilterChange={(key, value) =>
                          setFilterValues((prev) => ({ ...prev, [key]: value }))
                        }
                        onApply={applyFilters}
                        onClear={clearFilters}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="table-responsive mt-24 product-table">
                <table className="table table-striped custom-striped-table-product-list align-middle">
                  <thead>
                    <tr>
                      {["Date", "Module", "Action", "Reference", "Old Value", "New Value", "Created By"].map(
                        (heading) => (
                          <th key={heading} scope="col" style={{ fontSize: "15px", whiteSpace: "nowrap" }}>
                            {heading}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length > 0 ? (
                      currentItems.map((row) => (
                        <tr key={row.id || `${row.createTimestamp}-${row.referenceId}`}>
                          <td style={{ whiteSpace: "nowrap" }}>{formatDateTime(row.createTimestamp)}</td>
                          <td className="text-capitalize">{row.module || "-"}</td>
                          <td className="text-capitalize">{row.action || "-"}</td>
                          <td>{row.referenceId ?? "-"}</td>
                          <td style={{ maxWidth: "260px", wordBreak: "break-word" }}>{row.oldValue || "-"}</td>
                          <td style={{ maxWidth: "260px", wordBreak: "break-word" }}>{row.newValue || "-"}</td>
                          <td>{row.createdByName || row.createdBy || "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          No data found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-16 p-16">
                <div className="d-flex align-items-center">
                  <select
                    className="form-select d-inline-block w-auto"
                    value={itemsPerPage}
                    onChange={(e) => {
                      const val = e.target.value;
                      setItemsPerPage(val === "all" ? "all" : parseInt(val, 10));
                      setCurrentPage(1);
                    }}
                    style={{ height: "34px", fontSize: "14px", minWidth: "70px" }}
                  >
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="all">All</option>
                  </select>
                  <span className="ms-2">
                    Showing {showingFrom} to {showingTo} of {filteredData.length} Lists
                  </span>
                </div>
                <nav>
                  <ul className="pagination mt-0">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>
                        &laquo;
                      </button>
                    </li>
                    {Array.from({ length: totalPage }, (_, index) => index + 1)
                      .filter((pageNum) => Math.abs(pageNum - currentPage) <= 2)
                      .map((pageNum) => (
                        <li
                          key={pageNum}
                          className={`page-item ${currentPage === pageNum ? "active" : ""}`}
                        >
                          <button className="page-link" onClick={() => setCurrentPage(pageNum)}>
                            {pageNum}
                          </button>
                        </li>
                      ))}
                    <li className={`page-item ${currentPage === totalPage ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>
                        &raquo;
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
          <AdminFooter isSidebarMinimized={isSidebarMinimized} />
        </section>
      </div>
    </>
  );
};

const FilterPanel = ({ filterValues, onFilterChange, onApply, onClear }) => (
  <div
    style={{
      position: "absolute",
      top: "100%",
      left: 0,
      width: "500px",
      backgroundColor: "#fff",
      borderRadius: "8px",
      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
      zIndex: 1000,
      padding: "16px",
      marginTop: "10px",
    }}
  >
    <div className="row g-3">
      <div className="col-md-6">
        <input
          type={filterValues.fromDate ? "date" : "text"}
          className="form-control filter-input"
          placeholder="From Date"
          value={filterValues.fromDate}
          onFocus={(e) => {
            e.target.type = "date";
          }}
          onBlur={(e) => {
            if (!filterValues.fromDate) e.target.type = "text";
          }}
          onChange={(e) => onFilterChange("fromDate", e.target.value)}
        />
      </div>
      <div className="col-md-6">
        <input
          type={filterValues.toDate ? "date" : "text"}
          className="form-control filter-input"
          placeholder="To Date"
          value={filterValues.toDate}
          onFocus={(e) => {
            e.target.type = "date";
          }}
          onBlur={(e) => {
            if (!filterValues.toDate) e.target.type = "text";
          }}
          onChange={(e) => onFilterChange("toDate", e.target.value)}
        />
      </div>
    </div>
    <div className="d-flex gap-14 mt-24">
      <button
        onClick={onApply}
        style={{
          color: "#fff",
          backgroundColor: "#2b95d3",
          borderRadius: "6px",
          fontSize: "14px",
          fontWeight: "700",
          cursor: "pointer",
          padding: "8px 16px",
          minWidth: "150px",
          height: "40px",
          border: "none",
        }}
      >
        Apply Filters
      </button>
      <button
        onClick={onClear}
        style={{
          color: "#2692d2",
          backgroundColor: "#d9ebff",
          borderRadius: "6px",
          fontSize: "14px",
          fontWeight: "700",
          cursor: "pointer",
          padding: "8px 16px",
          minWidth: "150px",
          height: "40px",
          border: "none",
        }}
      >
        Clear All
      </button>
    </div>
  </div>
);

export default ListAuditLog;
